import { SpanStatusCode } from '@opentelemetry/api';
import {
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
} from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import Layout from 'components/layout';
import LettersPage from 'components/pages/letters/LettersPage';
import useLoadMoreLetters from 'hooks/themis/useLoadMoreLetters';
import shareImage from 'images/shares/trending.png';
import { getRecentLettersForState } from 'lib/api/themis/queries/letters';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import PropTypes from 'prop-types';
import { DATA_TYPES } from 'siteconfig';
import { themisResponsetoLetters } from 'utilities/letters';
import { OpenLetterTypeList } from 'utilities/propTypes';
import { getStateProperName, isValidStateCode } from 'utilities/states';

const LETTERS_TO_REQUEST = 25; // Initial load sets the page size for later paginated requests

const LettersForState = ({ openLetters, stateCode, initialNextToken }) => {
  const { letters, isLoadingMore, loadMoreLetters, loadingError, nextToken } =
    useLoadMoreLetters(openLetters, initialNextToken);

  return (
    <Layout
      title={`Letters to ${getStateProperName(stateCode)} Officials | Resistbot`}
      description={`Open letters delivered to ${getStateProperName(stateCode)} elected officials. See which representatives are hearing from constituents.`}
      image={shareImage}
      imageAltText='A stylized image of a woman with a megaphone shouting, over the words "we can do it"'
      canonicalPath={`/${DATA_TYPES.LETTER.basePath}/explore/${stateCode}`}
    >
      <LettersPage
        openLetters={letters}
        stateCode={stateCode}
        moreLettersCallback={loadMoreLetters}
        isLoadingMore={isLoadingMore}
        hasMore={!!nextToken}
      />

      {/* This is a bit rough. Duplicated in letters/explore/statecode */}
      {loadingError ? (
        <div className="flex justify-center">
          <p className="text-red-500">
            Error loading more letters: {loadingError}
          </p>
        </div>
      ) : null}
    </Layout>
  );
};

// NOTE: We have to load this live so that each user gets an individual token
// The way Timestream works, a token is used-up on each call so it can't be reused.
export const getServerSideProps = Sentry.wrapGetServerSidePropsWithSentry(
  async ({ params }) => {
    const stateCode = params.statecode.toUpperCase();

    const parentSpan = otelApi.trace.getActiveSpan();
    parentSpan.setAttributes({
      [ATTR_HTTP_ROUTE]: `/petitions/explore/${stateCode}`,
      'state.code': stateCode,
    });

    if (!isValidStateCode(stateCode)) {
      parentSpan.setStatus({
        code: SpanStatusCode.OK,
      });
      parentSpan.setAttributes({
        [ATTR_HTTP_RESPONSE_STATUS_CODE]: 404,
      });
      parentSpan.end();
      return {
        notFound: true,
      };
    }

    const recentLetters = await getRecentLettersForState({
      stateCode,
      limit: LETTERS_TO_REQUEST,
    });
    const lettersForList = themisResponsetoLetters(recentLetters.letters);

    return {
      props: {
        openLetters: lettersForList,
        initialNextToken: recentLetters.nextToken,
        stateCode: stateCode,
      },
    };
  }
);

LettersForState.propTypes = {
  openLetters: PropTypes.arrayOf(OpenLetterTypeList),
  initialNextToken: PropTypes.string,
  statecode: PropTypes.string,
};

export default LettersForState;
